
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Prompt, Category, GeoIntent, GeoUseCase, GeoOutputType, GeoDifficulty } from "@/lib/types";
import { useToast } from "@/hooks/use-toast";
import { TextFormattingToolbar } from "./TextFormattingToolbar";
import { CategoryCombobox } from "./CategoryCombobox";
import { 
  GeoIntentSelect, 
  GeoUseCaseSelect, 
  GeoModelSelect, 
  GeoTestedCheckbox,
  GeoOutputTypeSelect,
  GeoDifficultySelect,
  GeoKeywordsInput
} from "@/components/GEO";
import { createGeoMetadata } from "@/data/geo-constants";

type EditPromptDialogProps = {
  prompt: Prompt;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (updatedPrompt: Partial<Prompt>) => void;
};

export function EditPromptDialog({ prompt, open, onOpenChange, onSave }: EditPromptDialogProps) {
  const { toast } = useToast();
  const [title, setTitle] = useState(prompt.title);
  const [description, setDescription] = useState(prompt.description || "");
  const [content, setContent] = useState(prompt.content);
  const [category, setCategory] = useState<Exclude<Category, 'all'>>(prompt.category);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialCategory] = useState(prompt.category);
  const categoryEditCount = prompt.categoryEditCount || 0;
  const canEditCategory = categoryEditCount < 5;
  const categoryChanged = category !== initialCategory;
  
  // GEO Metadata state
  const [geoData, setGeoData] = useState({
    intent: (prompt.geoMetadata?.intent || "") as GeoIntent | "",
    useCase: (prompt.geoMetadata?.use_case || "") as GeoUseCase | "",
    recommendedModel: prompt.geoMetadata?.models?.recommended || "",
    testedByAuthor: prompt.geoMetadata?.reliability?.tested_by_author || false,
    outputType: (prompt.geoMetadata?.output_type || "") as GeoOutputType | "",
    difficulty: (prompt.geoMetadata?.difficulty || "") as GeoDifficulty | "",
    keywords: prompt.geoMetadata?.keywords || [] as string[],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast({
        title: "Title required",
        description: "Please enter a title for your prompt",
        variant: "destructive",
      });
      return;
    }

    if (!content.trim()) {
      toast({
        title: "Content required",
        description: "Please enter the content for your prompt",
        variant: "destructive",
      });
      return;
    }

    if (categoryChanged && !canEditCategory) {
      toast({
        title: "Category edit limit reached",
        description: "You have reached the maximum of 5 category edits for this prompt",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Build GEO metadata if intent and useCase are provided
      const geoMetadata = geoData.intent && geoData.useCase
        ? createGeoMetadata(
            geoData.intent as GeoIntent,
            geoData.useCase as GeoUseCase,
            geoData.recommendedModel || undefined,
            geoData.testedByAuthor,
            geoData.outputType as GeoOutputType || undefined,
            geoData.difficulty as GeoDifficulty || undefined,
            geoData.keywords
          )
        : undefined;

      const updatedPrompt: Partial<Prompt> = {
        title,
        description,
        content,
        category,
        categoryEditCount: categoryChanged ? categoryEditCount + 1 : categoryEditCount,
        geoMetadata,
      };
      
      onSave(updatedPrompt);
      onOpenChange(false);
      
      toast({
        title: "Prompt updated",
        description: "Your prompt has been successfully updated",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update prompt. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Prompt</DialogTitle>
          <DialogDescription>
            Enhance your prompt's AI discoverability with GEO metadata.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="title" className="text-sm font-medium">Title</label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a title for your prompt"
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="description" className="text-sm font-medium">
              Description <span className="text-destructive">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this prompt does (50-160 chars optimal for AI)"
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
            />
            <p className={`text-xs ${description.length >= 50 && description.length <= 160 ? 'text-green-600' : 'text-muted-foreground'}`}>
              {description.length}/300 · 50-160 chars optimal
            </p>
          </div>

          <div className="space-y-2">
            <label htmlFor="category" className="text-sm font-medium flex items-center justify-between">
              <span>Category <span className="text-muted-foreground font-normal">(optional)</span></span>
              <span className="text-xs text-muted-foreground">
                {canEditCategory ? `${5 - categoryEditCount} edits remaining` : 'Edit limit reached'}
              </span>
            </label>
            <CategoryCombobox
              value={category}
              onChange={(value) => setCategory(value as Exclude<Category, 'all'>)}
            />
            {!canEditCategory && categoryChanged && (
              <p className="text-xs text-destructive">
                You've reached the maximum of 5 category edits. You can still revert to the original category.
              </p>
            )}
          </div>

          <Separator className="my-4" />

          {/* GEO Metadata Section */}
          <Card className="border-border/50 bg-card/30">
            <CardHeader className="pb-3 pt-4">
              <CardTitle className="text-sm font-semibold">GEO Metadata</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pb-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium">
                    Intent <span className="text-destructive">*</span>
                  </label>
                  <GeoIntentSelect
                    value={geoData.intent}
                    onChange={(value) => setGeoData(prev => ({ ...prev, intent: value }))}
                  />
                </div>
                
                <div className="space-y-1.5">
                  <label className="text-xs font-medium">
                    Use Case <span className="text-destructive">*</span>
                  </label>
                  <GeoUseCaseSelect
                    value={geoData.useCase}
                    onChange={(value) => setGeoData(prev => ({ ...prev, useCase: value }))}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium">
                  Recommended Model <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <GeoModelSelect
                  value={geoData.recommendedModel}
                  onChange={(value) => setGeoData(prev => ({ ...prev, recommendedModel: value }))}
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium">
                    Output Type <span className="text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <GeoOutputTypeSelect
                    value={geoData.outputType}
                    onChange={(value) => setGeoData(prev => ({ ...prev, outputType: value }))}
                  />
                </div>
                
                <div className="space-y-1.5">
                  <label className="text-xs font-medium">
                    Difficulty <span className="text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <GeoDifficultySelect
                    value={geoData.difficulty}
                    onChange={(value) => setGeoData(prev => ({ ...prev, difficulty: value }))}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium">
                  Keywords <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <GeoKeywordsInput
                  value={geoData.keywords}
                  onChange={(keywords) => setGeoData(prev => ({ ...prev, keywords }))}
                />
              </div>

              <GeoTestedCheckbox
                checked={geoData.testedByAuthor}
                onChange={(checked) => setGeoData(prev => ({ ...prev, testedByAuthor: checked }))}
              />
            </CardContent>
          </Card>

          <Separator className="my-4" />
          
          <div className="space-y-2">
            <label htmlFor="content" className="text-sm font-medium">Content</label>
            <TextFormattingToolbar 
              value={content}
              onChange={setContent}
              className="w-full"
            />
          </div>
          
          <DialogFooter className="flex justify-end space-x-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
